/* eslint-disable no-nested-ternary */
import { useCallback, useRef, useMemo, useEffect, useState } from "react";
import moment from "moment";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGrin,
  faPaperclip,
  faPaperPlane,
  faPowerOff,
  faSpinner,
  faThumbsDown,
  faThumbsUp,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

import Picker from "emoji-picker-react";
import socketIOClient from "socket.io-client";

import FinishedClerk from "./FinishedClerk";
import { useChat } from "../../hooks/chat";
import Message from "./Message";

import "./styles.scss";

import {
  createMessageService,
  getChatService,
  readNotificationService,
  sendChatRate,
  closeChatService,
} from "../../services/deadpool";
import { ChatProperties } from "../../types";

type ChatProps = {
  setShowHelp: (value: boolean) => void;
  setIsChatOpen: (value: boolean) => void;
};

type TypingUsers = {
  user: string;
  user_name: string;
  typing: boolean;
};

type Messages = {
  firstMessage?: string;
  id: string;
  chat_id: string;
  author_uuid: string;
  author_name: string;
  author_email: string;
  is_whisper: null;
  file: null;
  text: string;
  createdAt: Date;
  updatedAt: Date;
};

const Chat: React.FC<ChatProps> = ({ setShowHelp, setIsChatOpen }) => {
  const {
    user,
    chat: { id },
    setKeyData,
    isExternal,
    notifications,
  } = useChat();

  const textRef = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const [typingUsers, setTypingUsers] = useState<TypingUsers[]>([]);
  const [messages, setMessages] = useState<Messages[]>([]);
  const [newRate, setNewRate] = useState<number | undefined>();
  const [file, setFile] = useState<any>(null);
  const [chat, setChat] = useState<ChatProperties>({} as ChatProperties);

  const [chatLoading, setChatLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [valueArea, setValueArea] = useState<string>("");

  const [cursorPosition, setCursorPosition] = useState<undefined | number>();
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const [disableTextArea, setDisableTextArea] = useState<boolean>(false);
  const [turnOffChat, setTurnOffChat] = useState<boolean>(false);
  const [finishedByAttendant, setFinishedByAttendant] =
    useState<boolean>(false);

  const finishChat = async () => {
    const response = await closeChatService(chat.id);
    setTurnOffChat(true);
    setIsChatOpen(response?.data.open);
  };

  const sendRate = async (rate: number) => {
    const res = await sendChatRate(id, rate);
    setNewRate(res?.data.rating);
  };

  const socket = useMemo(() => {
    const socket = socketIOClient(`${process.env.REACT_APP_DEADPOOL_URL}chat`, {
      query: {
        chat: id,
      },
      auth: {
        token: user?.uuid,
      },
      extraHeaders: {
        Authorization: `jwt ${user?.uuid}`,
      },
      forceNew: true,
    });

    socket?.on("message", msg => {
      if (!msg.message.is_whisper) {
        setMessages(data => [...(data as Array<any>), msg.message]);
      }
    });

    socket.on("responsible", ({ responsible_uuid, responsible_name }) => {
      if (responsible_uuid && responsible_name) {
        setMessages(data => [
          ...(data as Array<any>),
          { uuid: responsible_uuid, name: responsible_name },
        ]);
      }
    });

    socket?.on("open", chat => {
      setChat(chat);
      setFinishedByAttendant(true);
    });

    socket?.on("typing", data => {
      setTypingUsers(_users => {
        if (_users?.find(u => data.user === u.user)) {
          return _users?.map(u => (u.user === data.user ? data : u));
        }
        return [..._users, data];
      });
    });

    return socket;
  }, [id, user]);

  useEffect(() => {
    const [notification] = notifications?.filter(
      n => n.chat_id === id && n.user_uuid === user?.uuid && n.status === 0,
    );

    if (notification && id) {
      readNotificationService(notification?.id);

      const newNots = notifications.map(not =>
        not.id === notification.id ? { ...notification, status: 1 } : not,
      );

      setKeyData("notifications", newNots);
    }
  }, [notifications, id, user]);

  useEffect(() => {
    const _getChat = async () => {
      setChatLoading(true);
      try {
        const { data } = await getChatService(id);

        setMessages(data.messages.filter((msg: any) => !msg.is_whisper));
        setChat(data);
        setMessages(data => [
          ...(data as Array<any>),
          {
            firstMessage: "Por favor, aguarde um atendente...",
          },
        ]);
        setChatLoading(false);
      } catch (e) {
        setChatLoading(false);
        console.log(e);
      }
    };

    _getChat();
  }, [id, socket, newRate]);

  const sendMessage = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    if (chat && user && textRef?.current)
      try {
        const messagePayload = new FormData();

        messagePayload.append("chat_id", chat?.id);
        messagePayload.append("text", textRef.current.value);
        messagePayload.append("author_uuid", user.uuid);
        messagePayload.append("author_email", user.email);
        messagePayload.append("author_name", user.username);

        if (file) {
          messagePayload.append("file", file);
        }

        await createMessageService(messagePayload);

        textRef.current.value = "";
        socket?.emit("typing", {
          user: user?.uuid,
          user_name: user?.username,
          typing: false,
        });
        setLoading(false);
        setValueArea("");
        setFile(null);
        setShowEmojiPicker(false);
      } catch (e) {
        setLoading(false);
        console.log(e);
      }
  }, [chat, user, loading, textRef, socket, file]);

  const onChange = useCallback(
    e => {
      setValueArea(e.target.value);
      if (e.target.value.length === 1) {
        socket?.emit("typing", {
          user: user?.uuid,
          user_name: user?.username,
          typing: true,
        });
      } else if (e.target.value.length <= 0) {
        socket?.emit("typing", {
          user: user?.uuid,
          user_name: user?.username,
          typing: false,
        });
      }
    },
    [user, socket],
  );

  const submitOnEnter = useCallback(
    e => {
      if (e.ctrlKey && e.code === "Enter") {
        sendMessage();
      }
    },
    [sendMessage],
  );

  const onEmojiClick = (event: any, emojiObject: any) => {
    const ref = textRef.current;
    ref?.focus();
    const start = valueArea.substring(0, Number(ref?.selectionStart));
    const end = valueArea.substring(Number(ref?.selectionStart));
    const msg = start + emojiObject.emoji + end;
    setValueArea(msg);
    setCursorPosition(start.length + emojiObject.emoji.length);
  };

  useEffect(() => {
    setCursorPosition(textRef?.current?.selectionEnd);
  }, [cursorPosition]);

  useEffect(() => {
    if (Object.keys(chat).length === 0) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [chat]);

  return (
    <div className="chat">
      {showEmojiPicker && (
        <Picker
          onEmojiClick={onEmojiClick}
          disableSearchBar
          disableSkinTonePicker
          groupVisibility={{
            animals_nature: false,
            food_drink: false,
            travel_places: false,
            activities: false,
            objects: false,
            symbols: false,
            flags: false,
            recently_used: false,
          }}
          groupNames={{
            smileys_people: "Emojis",
          }}
        />
      )}
      {loading && isExternal && Object.keys(chat).length > 0 ? (
        <div
          style={{
            minHeight: "424px",
            display: "flex",
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <FontAwesomeIcon icon={faSpinner} spin size="lg" />
        </div>
      ) : (
        <>
          {(Object.keys(chat).length > 0 && !chat?.open && isExternal) ||
          turnOffChat ? (
            <div className="offChat">
              <FinishedClerk rate={chat?.rating} sendRate={sendRate} />
              {chat?.rating !== null && !isExternal ? (
                <div className="backButton">
                  <button
                    type="button"
                    onClick={() => {
                      setKeyData("chat", undefined);
                      setIsChatOpen(true);
                      setShowHelp(false);
                    }}
                  >
                    VOLTAR PARA O INÍCIO
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <div
                style={{ display: turnOffChat ? "none" : "flex" }}
                className="endedChat"
              >
                {!isExternal && (
                  <>
                    {Object.keys(chat).length > 0 &&
                      !chat?.open &&
                      !finishedByAttendant && (
                        <span className="dateOfEndChat">
                          {`Essa conversa foi finalizada no dia ${moment(
                            chat?.updatedAt?.substring(0, 10),
                          ).format(
                            "DD/MM/YYYY",
                          )} às  ${chat?.updatedAt?.substring(11, 16)} `}
                        </span>
                      )}
                  </>
                )}
                {Object.keys(chat).length > 0 &&
                  !isExternal &&
                  chat?.rating !== null &&
                  !finishedByAttendant && (
                    <div className="rating">
                      <span>Você avaliou essa conversa como</span>
                      {chat?.rating === 0 ? (
                        <FontAwesomeIcon color="#FB264B" icon={faThumbsDown} />
                      ) : (
                        <FontAwesomeIcon color="#29D975" icon={faThumbsUp} />
                      )}
                    </div>
                  )}
              </div>
              {(!chat?.open && !isExternal) ||
              (chat?.open && isExternal) ||
              (chat?.open && !isExternal) ? (
                <ul
                  style={{
                    maxHeight: finishedByAttendant ? "424px" : "350px",
                  }}
                  ref={ref => {
                    if (ref) {
                      ref.scrollTo({
                        top: ref.scrollHeight,
                        behavior: "smooth",
                      });
                    }
                  }}
                >
                  {chatLoading ? (
                    <div className="loadingChat">
                      <FontAwesomeIcon
                        size="lg"
                        color="#64728c"
                        icon={faSpinner}
                        spin={chatLoading}
                      />
                    </div>
                  ) : messages?.length ? (
                    messages?.map(
                      message =>
                        !message.is_whisper && (
                          <Message message={message} user={user} chat={chat} />
                        ),
                    )
                  ) : (
                    <span className="no_message" />
                  )}
                  {finishedByAttendant && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "20px",
                      }}
                    >
                      <FinishedClerk rate={chat?.rating} sendRate={sendRate} />
                      {chat?.rating !== null && !isExternal && (
                        <div className="backButton">
                          <button
                            type="button"
                            onClick={() => {
                              setKeyData("chat", undefined);
                              setIsChatOpen(true);
                              setShowHelp(false);
                            }}
                          >
                            VOLTAR PARA O INÍCIO
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </ul>
              ) : null}
              {chat?.open && (
                <footer style={{ gridGap: 0 }}>
                  {typingUsers?.find(
                    u => u.typing && u.user_name !== chat.owner_name,
                  ) && (
                    <div className="typing">
                      {typingUsers.map(
                        (u, i) =>
                          u.typing && (
                            <p key={u.user}>
                              {u.user_name}
                              {typingUsers.length > 1 &&
                              i + 1 !== typingUsers.length
                                ? ", "
                                : " "}
                            </p>
                          ),
                      )}
                    </div>
                  )}
                  {!disableTextArea ? (
                    <div className="inputContent">
                      {file && (
                        <div className="fileBox">
                          {file.name}
                          <FontAwesomeIcon
                            icon={faTimes}
                            onClick={() => setFile(null)}
                          />
                        </div>
                      )}
                      <div className="messageBox">
                        <textarea
                          disabled={!chat?.open}
                          value={valueArea}
                          ref={textRef}
                          onChange={onChange}
                          onKeyDown={submitOnEnter}
                          placeholder={
                            chat?.open ? "Digite aqui" : "Ticket fechado"
                          }
                        />
                        <FontAwesomeIcon
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          className="emoji"
                          icon={faGrin}
                        />
                        <input
                          ref={fileInput}
                          type="file"
                          id="fileinput"
                          style={{
                            display: "none",
                          }}
                        />
                        <input
                          ref={fileInput}
                          style={{ display: "none" }}
                          multiple
                          type="file"
                          id="raised-button-file"
                          onChange={(e: any) => setFile(e.target.files[0])}
                        />
                        <label className="clip" htmlFor="raised-button-file">
                          <FontAwesomeIcon icon={faPaperclip} />
                        </label>
                        <button
                          className="sendMessage"
                          type="button"
                          onClick={sendMessage}
                          disabled={loading}
                        >
                          <FontAwesomeIcon
                            icon={loading ? faSpinner : faPaperPlane}
                            spin={loading}
                          />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="turnOffChat">
                      <span>Você realmente deseja encerrar essa conversa?</span>
                      <div className="buttons">
                        <button
                          onClick={() => setDisableTextArea(false)}
                          className="cancelButton"
                          type="button"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={finishChat}
                          className="finishButton"
                          type="button"
                        >
                          Encerrar
                        </button>
                      </div>
                    </div>
                  )}
                  {!disableTextArea && (
                    <button
                      style={{
                        backgroundColor: "#ffff",
                      }}
                      type="button"
                      onClick={() => setDisableTextArea(true)}
                      className="finishChat"
                    >
                      <FontAwesomeIcon
                        style={{ color: "#fb264b" }}
                        icon={faPowerOff}
                      />
                      <span style={{ color: "#fb264b" }}>
                        Encerrar conversa
                      </span>
                    </button>
                  )}
                </footer>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Chat;
